import { useState, useEffect, useRef, useCallback } from 'react';

export default function FloatingWhatsApp() {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [initialized, setInitialized] = useState(false);
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, elX: 0, elY: 0 });
  const barRef = useRef<HTMLDivElement>(null);
  const isMobile = useRef(false);

  useEffect(() => {
    isMobile.current = window.innerWidth < 768;
    const timer = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(timer);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    dragging.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      elX: pos.x,
      elY: pos.y,
    };
    if (barRef.current) {
      barRef.current.setPointerCapture(e.pointerId);
    }
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPos({ x: dragStart.current.elX + dx, y: dragStart.current.elY + dy });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    if (!initialized && visible) {
      setInitialized(true);
      if (isMobile.current) {
        setPos({ x: 0, y: 0 });
      }
    }
  }, [visible, initialized]);

  const isDragged = pos.x !== 0 || pos.y !== 0;
  const translateStyle = isDragged
    ? { transform: `translate(${pos.x}px, ${pos.y}px)` }
    : {};

  return (
    <div
      ref={barRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        ...translateStyle,
        touchAction: 'none',
        userSelect: 'none',
      }}
      className={`fixed bottom-5 left-1/2 md:bottom-8 md:right-8 md:left-auto z-50 transition-all duration-500 cursor-grab active:cursor-grabbing ${
        visible ? 'opacity-100' : 'opacity-0 translate-y-4'
      } ${!isDragged ? '-translate-x-1/2 md:translate-x-0' : ''}`}
    >
      <div className="flex items-center gap-1.5 bg-white rounded-full shadow-lg border border-background-200 px-2 py-2 md:px-3 md:py-2.5">
        <a
          href="tel:698084044"
          aria-label="Zadzwoń"
          draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-primary-500 text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-phone-line text-lg md:text-xl" />
        </a>
        <a
          href="https://wa.me/48698084044"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-whatsapp-line text-lg md:text-xl" />
        </a>
        <a
          href="/kontakt"
          aria-label="Napisz"
          draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-accent-500 text-white flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer"
        >
          <i className="ri-mail-line text-lg md:text-xl" />
        </a>
      </div>
    </div>
  );
}